import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, X, Search, CheckSquare, Square, Users } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { useComprobante, useCreateComprobante, useUpdateComprobante } from '../../hooks/useComprobantes';
import { useProducts } from '../../hooks/useProducts';
import { useClients } from '../../hooks/useClients';
import { useDebounce } from '../../hooks/useDebounce';
import { formatCurrency } from '../../lib/utils';

interface ProductoComprobante {
  id: string;
  nombre: string;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
}

export function ComprobanteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: comprobante, isLoading: comprobanteLoading } = useComprobante(id ? parseInt(id) : 0);
  const { data: clientsData } = useClients({ limit: 1000 });
  const clients = clientsData?.data ?? [];
  const createComprobante = useCreateComprobante();
  const updateComprobante = useUpdateComprobante();

  const [searchCliente, setSearchCliente] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any>(null);
  const [autocompletar, setAutocompletar] = useState(true);

  const [formData, setFormData] = useState({
    clienteNombre: '',
    clienteDocumento: '',
    clienteLocalidad: '',
    clienteDireccion: '',
    clienteProvincia: '',
    clienteCp: '',
    costoEnvio: 0,
    descuentoPorcentaje: 0,
    observaciones: '',
  });

  const [productos, setProductos] = useState<ProductoComprobante[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [cantidad, setCantidad] = useState(1);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const shouldSearchProducts =
    searchTerm.trim().length > 0 &&
    (!selectedProduct || selectedProduct.nombre !== searchTerm);

  const { data: productsData, isLoading: loadingProducts } = useProducts({
    search: shouldSearchProducts ? debouncedSearchTerm.trim() : undefined,
    limit: 20,
    activo: true,
  });

  const filteredProducts = productsData?.data ?? [];

  useEffect(() => {
    if (comprobante && isEditing) {
      setFormData({
        clienteNombre: comprobante.clienteNombre || '',
        clienteDocumento: comprobante.clienteDocumento || '',
        clienteLocalidad: comprobante.clienteLocalidad || '',
        clienteDireccion: comprobante.clienteDireccion || '',
        clienteProvincia: comprobante.clienteProvincia || '',
        clienteCp: comprobante.clienteCp || '',
        costoEnvio: Number(comprobante.costoEnvio) || 0,
        descuentoPorcentaje: Number(comprobante.promocionPorcentaje) || 0,
        observaciones: comprobante.observaciones || '',
      });

      if (comprobante.productosJson) {
        try {
          const productosData = typeof comprobante.productosJson === 'string'
            ? JSON.parse(comprobante.productosJson)
            : comprobante.productosJson;
          setProductos(productosData.map((p: any) => ({
            id: p.id || p.codigo || `p-${Date.now()}`,
            nombre: p.nombre,
            cantidad: p.cantidad || 1,
            precioUnitario: p.precioUnitario || 0,
            subtotal: (p.cantidad || 1) * (p.precioUnitario || 0),
          })));
        } catch {
          // ignorar error de parseo
        }
      }

      if (comprobante.clienteId) {
        const cliente = clients.find((c: any) => c.id === comprobante.clienteId);
        if (cliente) {
          setClienteSeleccionado(cliente);
          setSearchCliente(cliente.nombre);
        }
      }
    }
  }, [comprobante, isEditing, clients]);

  const handleSelectClient = (client: any) => {
    setClienteSeleccionado(client);
    setSearchCliente(client.nombre);
    setShowClienteDropdown(false);

    setFormData(prev => ({
      ...prev,
      clienteNombre: client.nombre,
      clienteDocumento: autocompletar ? (client.cuil || client.documento || '') : prev.clienteDocumento,
      clienteLocalidad: autocompletar ? (client.localidad || '') : prev.clienteLocalidad,
      clienteDireccion: autocompletar ? (client.direccion || '') : prev.clienteDireccion,
      clienteProvincia: autocompletar ? (client.provincia || '') : prev.clienteProvincia,
      clienteCp: autocompletar ? (client.cp || client.codigoPostal || '') : prev.clienteCp,
    }));

    toast.success(`Cliente seleccionado: ${client.nombre}`);
  };

  const toggleAutocompletar = () => {
    const nuevoEstado = !autocompletar;
    setAutocompletar(nuevoEstado);

    if (nuevoEstado && clienteSeleccionado) {
      setFormData(prev => ({
        ...prev,
        clienteDocumento: clienteSeleccionado.cuil || clienteSeleccionado.documento || '',
        clienteLocalidad: clienteSeleccionado.localidad || '',
        clienteDireccion: clienteSeleccionado.direccion || '',
        clienteProvincia: clienteSeleccionado.provincia || '',
        clienteCp: clienteSeleccionado.cp || clienteSeleccionado.codigoPostal || '',
      }));
    }
  };

  const filteredClients = clients.filter((c: any) => {
    const searchLower = searchCliente.toLowerCase();
    const nombreMatch = c.nombre?.toLowerCase().includes(searchLower) || false;
    const telefonoMatch = c.telefono?.toLowerCase().includes(searchLower) || false;
    return nombreMatch || telefonoMatch;
  }) || [];

  const handleAddProduct = () => {
    if (!selectedProduct) {
      toast.error('Selecciona un producto primero');
      return;
    }

    if (cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    const newProduct: ProductoComprobante = {
      id: selectedProduct.id ? String(selectedProduct.id) : `p-${Date.now()}`,
      nombre: selectedProduct.nombre,
      cantidad: cantidad,
      precioUnitario: Number(selectedProduct.precio) || 0,
      subtotal: cantidad * (Number(selectedProduct.precio) || 0),
    };

    const exists = productos.some(p => p.id === newProduct.id);
    if (exists) {
      toast.error('El producto ya está agregado');
      return;
    }

    setProductos([...productos, newProduct]);
    setSelectedProduct(null);
    setSearchTerm('');
    setCantidad(1);
    setShowDropdown(false);
    toast.success('Producto agregado');
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSearchTerm('');
    setCantidad(1);
    setShowDropdown(false);
  };

  const handleRemoveProduct = (index: number) => {
    setProductos(productos.filter((_, i) => i !== index));
  };

  const handleCantidadChange = (index: number, newCantidad: number) => {
    const updated = [...productos];
    updated[index].cantidad = newCantidad;
    updated[index].subtotal = newCantidad * updated[index].precioUnitario;
    setProductos(updated);
  };

  const subtotal = productos.reduce((sum, p) => sum + p.subtotal, 0);
  const descuentoMonto = subtotal * (formData.descuentoPorcentaje / 100);
  const total = subtotal - descuentoMonto + (formData.costoEnvio || 0);

  const isFieldDisabled = !!clienteSeleccionado && autocompletar;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.clienteNombre.trim().length < 2) {
      toast.error('El nombre del cliente debe tener al menos 2 caracteres');
      return;
    }
    if (formData.clienteLocalidad.trim().length < 2) {
      toast.error('La localidad debe tener al menos 2 caracteres');
      return;
    }
    if (formData.clienteDireccion.trim().length < 2) {
      toast.error('La dirección debe tener al menos 2 caracteres');
      return;
    }
    if (productos.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }
    if (productos.some(p => p.cantidad < 1)) {
      toast.error('Todos los productos deben tener una cantidad mínima de 1');
      return;
    }

    try {
      const payload = {
        ...formData,
        clienteId: clienteSeleccionado?.id || null,
        productos: productos.map(p => ({
          id: p.id ? String(p.id) : undefined,
          nombre: p.nombre,
          cantidad: p.cantidad,
          precioUnitario: p.precioUnitario,
        })),
        descuentoPorcentaje: formData.descuentoPorcentaje,
        costoEnvio: formData.costoEnvio,
      };

      if (isEditing && id) {
        await updateComprobante.mutateAsync({ id: parseInt(id), data: payload });
      } else {
        await createComprobante.mutateAsync(payload);
      }
      navigate('/comprobantes');
    } catch (error: any) {
      console.error('Error detallado:', error);
      toast.error(error.response?.data?.error || 'Error al guardar el comprobante');
    }
  };

  if (comprobanteLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando comprobante...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/comprobantes')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Comprobante' : 'Nuevo Comprobante'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna izquierda */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Users size={20} className="text-blue-500" />
                Datos del Cliente
              </h2>

              {/* Buscador de cliente */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buscar Cliente Existente
                </label>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Buscar cliente por nombre o teléfono..."
                    value={searchCliente}
                    onChange={(e) => {
                      setSearchCliente(e.target.value);
                      setShowClienteDropdown(true);
                      if (!e.target.value) {
                        setClienteSeleccionado(null);
                        setFormData(prev => ({
                          ...prev,
                          clienteNombre: '',
                          clienteDocumento: '',
                          clienteLocalidad: '',
                          clienteDireccion: '',
                          clienteProvincia: '',
                          clienteCp: '',
                        }));
                      }
                    }}
                    onFocus={() => setShowClienteDropdown(true)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchCliente && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchCliente('');
                        setClienteSeleccionado(null);
                        setFormData(prev => ({
                          ...prev,
                          clienteNombre: '',
                          clienteDocumento: '',
                          clienteLocalidad: '',
                          clienteDireccion: '',
                          clienteProvincia: '',
                          clienteCp: '',
                        }));
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}

                  {/* Dropdown */}
                  {showClienteDropdown && searchCliente && filteredClients.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.map((client: any) => (
                        <button
                          key={client.id}
                          type="button"
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                          onClick={() => handleSelectClient(client)}
                        >
                          <div>
                            <p className="font-medium text-gray-800">{client.nombre}</p>
                            {client.telefono && (
                              <p className="text-xs text-gray-400">{client.telefono}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Toggle de autocompletado */}
              {clienteSeleccionado && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <button
                    type="button"
                    onClick={toggleAutocompletar}
                    className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
                  >
                    {autocompletar ? (
                      <CheckSquare size={18} className="text-blue-600" />
                    ) : (
                      <Square size={18} className="text-blue-400" />
                    )}
                    {autocompletar ? 'Autocompletar datos activado' : 'Autocompletar datos desactivado'}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo *
                  </label>
                  <Input
                    value={formData.clienteNombre}
                    onChange={(e) => setFormData({ ...formData, clienteNombre: e.target.value })}
                    placeholder="Ej: Juan Pérez"
                    required
                    disabled={isFieldDisabled}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Documento
                  </label>
                  <Input
                    value={formData.clienteDocumento}
                    onChange={(e) => setFormData({ ...formData, clienteDocumento: e.target.value })}
                    placeholder="Ej: 12345678"
                    disabled={isFieldDisabled}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Localidad *
                  </label>
                  <Input
                    value={formData.clienteLocalidad}
                    onChange={(e) => setFormData({ ...formData, clienteLocalidad: e.target.value })}
                    placeholder="Ej: San Andrés de Giles"
                    required
                    disabled={isFieldDisabled}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección *
                  </label>
                  <Input
                    value={formData.clienteDireccion}
                    onChange={(e) => setFormData({ ...formData, clienteDireccion: e.target.value })}
                    placeholder="Ej: Av. Tecnología 123"
                    required
                    disabled={isFieldDisabled}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provincia
                  </label>
                  <Input
                    value={formData.clienteProvincia}
                    onChange={(e) => setFormData({ ...formData, clienteProvincia: e.target.value })}
                    placeholder="Ej: Buenos Aires"
                    disabled={isFieldDisabled}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código Postal
                  </label>
                  <Input
                    value={formData.clienteCp}
                    onChange={(e) => setFormData({ ...formData, clienteCp: e.target.value })}
                    placeholder="Ej: 6720"
                    disabled={isFieldDisabled}
                  />
                </div>
              </div>
            </Card>

            {/* Productos */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Productos</h2>

              <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="text"
                    placeholder="Buscar producto..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                      if (selectedProduct && e.target.value !== selectedProduct.nombre) {
                        setSelectedProduct(null);
                      }
                    }}
                    onFocus={() => setShowDropdown(true)}
                    className="pl-10 pr-10"
                  />

                  {selectedProduct && (
                    <button
                      type="button"
                      onClick={handleClearProduct}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Quitar producto seleccionado"
                    >
                      <X size={16} />
                    </button>
                  )}

                  {/* Dropdown */}
                  {showDropdown && searchTerm && filteredProducts.length > 0 && (
                    <div
                      className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl"
                      style={{
                        maxHeight: '200px',
                        height: 'auto',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        scrollBehavior: 'smooth'
                      }}
                    >
                      {filteredProducts.map((product: any) => {
                        const truncateDescription = (text: string) => {
                          if (!text) return '';
                          if (text.length <= 60) return text;
                          return text.substring(0, 60) + '.....';
                        };

                        return (
                          <button
                            key={product.id}
                            type="button"
                            className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors flex justify-between items-center border-b border-gray-100 last:border-0"
                            onClick={() => {
                              setSelectedProduct(product);
                              setSearchTerm(product.nombre);
                              setShowDropdown(false);
                            }}
                          >
                            <div className="flex-1 min-w-0 pr-3">
                              <p className="font-medium text-gray-800 truncate">{product.nombre}</p>
                              {product.descripcion && (
                                <p className="text-xs text-gray-400 truncate">
                                  {truncateDescription(product.descripcion)}
                                </p>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-blue-600 flex-shrink-0">
                              {formatCurrency(product.precio)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Estado de carga */}
                  {showDropdown && searchTerm && loadingProducts && (
                    <div className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      Buscando productos...
                    </div>
                  )}

                  {/* Sin resultados */}
                  {showDropdown && searchTerm && !loadingProducts && filteredProducts.length === 0 && (
                    <div className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      No se encontraron productos
                    </div>
                  )}
                </div>

                <div className="w-24">
                  <Input
                    type="number"
                    value={cantidad}
                    onChange={(e) => setCantidad(Number(e.target.value) || 0)}
                    min={1}
                  />
                </div>

                <Button
                  type="button"
                  onClick={handleAddProduct}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus size={18} />
                  Agregar
                </Button>
              </div>

              {/* Lista de productos seleccionados */}
              <div className="space-y-2">
                {productos.map((producto, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{producto.nombre}</p>
                      <p className="text-sm text-gray-500">
                        {formatCurrency(producto.precioUnitario)} x {producto.cantidad}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-20">
                        <Input
                          type="number"
                          value={producto.cantidad}
                          onChange={(e) => handleCantidadChange(index, Number(e.target.value) || 0)}
                          min={1}
                          className="text-center"
                        />
                      </div>
                      <p className="font-semibold text-gray-800 min-w-[80px] text-right">
                        {formatCurrency(producto.subtotal)}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleRemoveProduct(index)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                ))}

                {productos.length === 0 && (
                  <p className="text-center text-gray-400 py-4">
                    No hay productos agregados
                  </p>
                )}
              </div>
            </Card>
          </div>

          {/* Columna derecha */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Condiciones</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Costo de Envío
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.costoEnvio}
                    onChange={(e) => setFormData({ ...formData, costoEnvio: Number(e.target.value) || 0 })}
                    min={0}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Promoción (%)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.descuentoPorcentaje}
                    onChange={(e) => setFormData({ ...formData, descuentoPorcentaje: Number(e.target.value) || 0 })}
                    min={0}
                    max={100}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observaciones
                  </label>
                  <textarea
                    value={formData.observaciones}
                    onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                    placeholder="Observaciones adicionales..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Resumen</h2>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">{formatCurrency(subtotal)}</span>
                </div>

                {formData.descuentoPorcentaje > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Descuento ({formData.descuentoPorcentaje}%)</span>
                    <span>-{formatCurrency(descuentoMonto)}</span>
                  </div>
                )}

                {formData.costoEnvio > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Costo de Envío</span>
                    <span className="font-medium">{formatCurrency(formData.costoEnvio)}</span>
                  </div>
                )}

                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-blue-600">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                <Save size={18} />
                {isEditing ? 'Actualizar Comprobante' : 'Crear Comprobante'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/comprobantes')}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}